// Centralized Firebase Functions Service
// Automatically detects environment and provides correct function URLs

import { environment } from '../config/firebase.config';

// Helper to determine if we're running locally vs on Vercel dev
const isLocalDevelopment = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    // Check for local network IPs for mobile device testing
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(hostname)
  );
};

// Helper to get the correct emulator host
const getEmulatorHost = () => {
  const hostname = window.location.hostname;
  // Use the same host as the current page when accessed via local network
  return (hostname === 'localhost' || hostname === '127.0.0.1')
    ? '127.0.0.1'
    : hostname;
};

const functionConfigs = {
  development: {
    // For local development: use emulators
    // For Vercel development: use deployed development functions
    baseUrl: isLocalDevelopment()
      ? `http://${getEmulatorHost()}:5001/realsightseerbackend-dev/europe-west8`
      : 'https://europe-west8-realsightseerbackend-dev.cloudfunctions.net',
    endpoints: {
      // Local development uses relative paths, Vercel dev uses Cloud Run URLs
      createCheckoutSession: isLocalDevelopment()
        ? '/createCheckoutSession'
        : 'https://createcheckoutsession-gzx5mixtpa-oc.a.run.app',
      stripeWebhook: isLocalDevelopment()
        ? '/stripeWebhook'
        : 'https://stripewebhook-gzx5mixtpa-oc.a.run.app',
      historyAudio: isLocalDevelopment()
        ? '/historyAudio'
        : 'https://historyaudio-gzx5mixtpa-oc.a.run.app',
      getLocationSightsGemini31FlashLitePreview: isLocalDevelopment()
        ? '/getLocationSightsGemini31FlashLitePreview'
        : '/getLocationSightsGemini31FlashLitePreview',
      getHistoricalPhasesMarkdownList: isLocalDevelopment()
        ? '/getHistoricalPhasesMarkdownList'
        : 'https://gethistoricalphasesmarkdownlist-gzx5mixtpa-oc.a.run.app',
      streamRegionalDishes: isLocalDevelopment()
        ? '/getRegionalDishesOpenrouter'
        : 'https://europe-west8-realsightseerbackend-dev.cloudfunctions.net/getRegionalDishesOpenrouter',
      getRegionalDishes: isLocalDevelopment()
        ? '/getRegionalDishesOpenrouter'
        : 'https://europe-west8-realsightseerbackend-dev.cloudfunctions.net/getRegionalDishesOpenrouter',
      getRegionalDishesOpenrouter: isLocalDevelopment()
        ? '/getRegionalDishesOpenrouter'
        : 'https://europe-west8-realsightseerbackend-dev.cloudfunctions.net/getRegionalDishesOpenrouter',
      getInterestingAspects: isLocalDevelopment()
        ? '/getInterestingAspects'
        : 'https://getinterestingaspects-gzx5mixtpa-oc.a.run.app',
      getLocationUnderstandingConcepts: '/getLocationUnderstandingConcepts',
      getLocationUnderstandingConceptsGemini: '/getLocationUnderstandingConceptsGemini',
      generateAnecdotes: isLocalDevelopment()
        ? '/generateAnecdotes'
        : 'https://generateanecdotes-gzx5mixtpa-oc.a.run.app',
      verifyPlayPurchase: isLocalDevelopment()
        ? '/verifyPlayPurchase'
        : 'https://verifyplaypurchase-gzx5mixtpa-oc.a.run.app',
      resolvePendingPayment: isLocalDevelopment()
        ? '/resolvePendingPayment'
        : 'https://resolvependingpayment-gzx5mixtpa-oc.a.run.app',
    }
  },
  production: {
    // Production Cloud Functions URLs
    baseUrl: 'https://europe-west8-realsightseerbackend.cloudfunctions.net',
    endpoints: {
      createCheckoutSession: 'https://createcheckoutsession-kicpyfq6mq-oc.a.run.app',
      stripeWebhook: '/stripeWebhook',
      historyAudio: 'https://historyaudio-kicpyfq6mq-oc.a.run.app',
      getLocationSightsGemini31FlashLitePreview: '/getLocationSightsGemini31FlashLitePreview',
      getHistoricalPhasesMarkdownList: 'https://gethistoricalphasesmarkdownlist-kicpyfq6mq-oc.a.run.app',
      streamRegionalDishes: 'https://europe-west8-realsightseerbackend.cloudfunctions.net/getRegionalDishesOpenrouter',
      getRegionalDishes: 'https://europe-west8-realsightseerbackend.cloudfunctions.net/getRegionalDishesOpenrouter',
      getRegionalDishesOpenrouter: 'https://europe-west8-realsightseerbackend.cloudfunctions.net/getRegionalDishesOpenrouter',
      getInterestingAspects: 'https://getinterestingaspects-kicpyfq6mq-oc.a.run.app',
      getLocationUnderstandingConcepts: '/getLocationUnderstandingConcepts',
      getLocationUnderstandingConceptsGemini: '/getLocationUnderstandingConceptsGemini',
      generateAnecdotes: 'https://generateanecdotes-kicpyfq6mq-oc.a.run.app',
      verifyPlayPurchase: 'https://verifyplaypurchase-kicpyfq6mq-oc.a.run.app',
      resolvePendingPayment: 'https://resolvependingpayment-kicpyfq6mq-oc.a.run.app',
    }
  }
};

const config = functionConfigs[environment];

// Helper function to get complete function URL
export const getFunctionUrl = (functionName) => {
  const endpoint = config.endpoints[functionName];

  if (!endpoint) {
    console.error(`[FirebaseFunctions] Unknown function: ${functionName}`);
    return null;
  }

  // If endpoint starts with https://, it's a complete URL (production or Vercel dev)
  if (endpoint.startsWith('https://')) {
    return endpoint;
  }

  // Otherwise, combine with base URL (local development emulators)
  return `${config.baseUrl}${endpoint}`;
};

// Export individual function URLs for convenience
export const functionUrls = {
  createCheckoutSession: getFunctionUrl('createCheckoutSession'),
  stripeWebhook: getFunctionUrl('stripeWebhook'),
  historyAudio: getFunctionUrl('historyAudio'),
  getLocationSightsGemini31FlashLitePreview: getFunctionUrl('getLocationSightsGemini31FlashLitePreview'),
  getHistoricalPhasesMarkdownList: getFunctionUrl('getHistoricalPhasesMarkdownList'),
  streamRegionalDishes: getFunctionUrl('streamRegionalDishes'),
  getRegionalDishes: getFunctionUrl('getRegionalDishes'),
  getRegionalDishesOpenrouter: getFunctionUrl('getRegionalDishesOpenrouter'),
  getInterestingAspects: getFunctionUrl('getInterestingAspects'),
  getLocationUnderstandingConcepts: getFunctionUrl('getLocationUnderstandingConcepts'),
  getLocationUnderstandingConceptsGemini: getFunctionUrl('getLocationUnderstandingConceptsGemini'),
  generateAnecdotes: getFunctionUrl('generateAnecdotes'),
  verifyPlayPurchase: getFunctionUrl('verifyPlayPurchase'),
  resolvePendingPayment: getFunctionUrl('resolvePendingPayment')
};

// Function wakeup utility
export const wakeupFunctions = async () => {
  const functionsToWakeup = [
    'historyAudio',
    'getHistoricalPhasesMarkdownList',
    'getInterestingAspects',
    'getLocationUnderstandingConcepts',
    'getLocationUnderstandingConceptsGemini',
    'generateAnecdotes',
    'getLocationSightsGemini31FlashLitePreview',
    'getRegionalDishes',
    'streamRegionalDishes',
    'getRegionalDishesOpenrouter'
  ];

  console.log('[FirebaseFunctions] Starting function wakeup...');

  const wakeupPromises = functionsToWakeup.map(async (functionName) => {
    try {
      const url = getFunctionUrl(functionName);
      if (!url) {
        console.warn(`[FirebaseFunctions] Cannot wakeup ${functionName}: URL not found`);
        return;
      }

      const response = await fetch(url + '?wakeup=true', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log(`[FirebaseFunctions] Successfully woke up ${functionName}`);
      } else {
        console.warn(`[FirebaseFunctions] Failed to wake up ${functionName}: ${response.status}`);
      }
    } catch (error) {
      console.warn(`[FirebaseFunctions] Error waking up ${functionName}:`, error.message);
    }
  });

  // Execute all wakeup calls in parallel
  await Promise.allSettled(wakeupPromises);
  console.log('[FirebaseFunctions] Function wakeup completed');
};

// Log current environment and URLs for debugging
console.log(`[FirebaseFunctions] Using ${environment} environment`);
console.log(`[FirebaseFunctions] Local development: ${isLocalDevelopment()}`);
console.log(`[FirebaseFunctions] Base URL: ${config.baseUrl}`);
console.log(`[FirebaseFunctions] Sample function URL: ${getFunctionUrl('historyAudio')}`);

export { environment };
export default functionUrls;
